import React from "react";
import { Link } from "react-router";

import { ZodProvider } from "@autoform/zod";
import { ChevronDownIcon, ExternalLinkIcon } from "lucide-react";

import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import { tagRuleEditSchema } from "@/pages/tina/tag-rules/schemas/tag-rule.form";
import { useTagSuggestions } from "@/pages/tina/tag-suggestions/hooks/use-tag-suggestions";
import type {
  TagSuggestionExample,
  TagSuggestionRead,
  TagSuggestionStatus,
} from "@/types/tina";

const STATUSES: ReadonlyArray<{ value: TagSuggestionStatus; label: string }> = [
  { value: "open", label: "Open" },
  { value: "snoozed", label: "Snoozed" },
  { value: "accepted", label: "Accepted" },
];

const MAX_EXAMPLES = 5;

// The accept body takes only these two fields (extra keys are rejected).
const acceptSchema = tagRuleEditSchema.pick({
  tag_name: true,
  explanation: true,
});
const acceptSchemaProvider = new ZodProvider(acceptSchema);

const isStatus = (value: string): value is TagSuggestionStatus =>
  STATUSES.some((entry) => entry.value === value);

const formatDate = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
};

export const TagSuggestionsList = () => {
  const [status, setStatus] = usePersistedState<TagSuggestionStatus>(
    "tina-tag-suggestions:status",
    "open",
  );
  const { suggestions, isLoading, accept, snooze } = useTagSuggestions(status);
  const [accepting, setAccepting] = React.useState<TagSuggestionRead | null>(
    null,
  );
  const [busyId, setBusyId] = React.useState<number | null>(null);

  const ready = suggestions.filter((s) => s.ready);
  const notReady = suggestions.filter((s) => !s.ready);
  const actionable = status === "open" || status === "snoozed";

  const onSnooze = async (suggestion: TagSuggestionRead) => {
    setBusyId(suggestion.id);
    await snooze(suggestion.id);
    setBusyId(null);
  };

  const renderCard = (suggestion: TagSuggestionRead) => (
    <SuggestionCard
      key={suggestion.id}
      suggestion={suggestion}
      busy={busyId === suggestion.id}
      actionable={actionable}
      onCreate={() => setAccepting(suggestion)}
      onSnooze={() => onSnooze(suggestion)}
    />
  );

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Tag suggestions</h1>
        <p className="text-xs text-muted-foreground">
          New tags the signals stage keeps proposing. Create a tag rule to
          accept one, or snooze it for 90 days.
        </p>
      </div>
      <Tabs
        value={status}
        onValueChange={(value) => isStatus(value) && setStatus(value)}
      >
        <TabsList>
          {STATUSES.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : suggestions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No suggestions.</p>
      ) : status === "open" ? (
        <>
          {ready.length > 0 ? (
            <div className="flex flex-col gap-3">{ready.map(renderCard)}</div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nothing is ready yet.
            </p>
          )}
          {notReady.length > 0 ? (
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  <ChevronDownIcon className="h-4 w-4" />
                  Not ready yet ({notReady.length})
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 flex flex-col gap-3">
                {notReady.map(renderCard)}
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </>
      ) : (
        <div className="flex flex-col gap-3">{suggestions.map(renderCard)}</div>
      )}

      <Dialog
        open={accepting !== null}
        onOpenChange={(open) => !open && setAccepting(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create tag rule</DialogTitle>
            <DialogDescription>
              Saves the rule and closes this suggestion.
            </DialogDescription>
          </DialogHeader>
          {accepting ? (
            <AutoForm
              key={accepting.id}
              schema={acceptSchemaProvider}
              defaultValues={{
                tag_name: accepting.name,
                explanation: accepting.description,
              }}
              onSubmit={async (values) => {
                const ok = await accept(accepting.id, {
                  tag_name: values.tag_name.trim(),
                  explanation: values.explanation,
                });
                if (ok) setAccepting(null);
              }}
              {...injectCustomAutoFormComponents()}
            >
              <div className="flex justify-end pt-2">
                <Button type="submit">Create tag rule</Button>
              </div>
            </AutoForm>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface SuggestionCardProps {
  suggestion: TagSuggestionRead;
  busy: boolean;
  actionable: boolean;
  onCreate: () => void;
  onSnooze: () => void;
}

const SuggestionCard = ({
  suggestion,
  busy,
  actionable,
  onCreate,
  onSnooze,
}: SuggestionCardProps) => {
  const { stats } = suggestion;
  return (
    <Card>
      <CardHeader className="gap-1">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-lg">{suggestion.name}</CardTitle>
            <CardDescription>{suggestion.description}</CardDescription>
          </div>
          {actionable ? (
            <div className="flex shrink-0 gap-2">
              <Button size="sm" onClick={onCreate} disabled={busy}>
                Create tag rule
              </Button>
              {suggestion.status === "open" ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onSnooze}
                  disabled={busy}
                >
                  Snooze 90 days
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground">
          <Badge variant="secondary">
            {stats.hits_window} events in the recent window
          </Badge>
          <span>{stats.hits_total} total</span>
          {stats.top_cities.map((c) => (
            <Badge key={c.city} variant="outline" className="font-normal">
              {c.city} {c.count}
            </Badge>
          ))}
          {suggestion.status === "snoozed" && suggestion.snoozed_until ? (
            <span>Snoozed until {formatDate(suggestion.snoozed_until)}</span>
          ) : null}
        </div>
      </CardHeader>
      {suggestion.examples.length > 0 ? (
        <CardContent>
          <ul className="flex flex-col gap-1 text-sm">
            {suggestion.examples.slice(0, MAX_EXAMPLES).map((example) => (
              <ExampleRow key={example.event_id} example={example} />
            ))}
          </ul>
        </CardContent>
      ) : null}
    </Card>
  );
};

const ExampleRow = ({ example }: { example: TagSuggestionExample }) => {
  const label = example.name ?? example.event_id;
  const meta = [example.venue, formatDate(example.start)]
    .filter(Boolean)
    .join(" · ");
  return (
    <li className="flex flex-wrap items-center gap-2">
      {example.admin_event_id ? (
        <Link
          to={`/events/edit/${example.admin_event_id}`}
          className="font-medium underline-offset-2 hover:underline"
        >
          {label}
        </Link>
      ) : (
        <span className="font-medium">{label}</span>
      )}
      {meta ? <span className="text-muted-foreground">{meta}</span> : null}
      {example.url ? (
        <a
          href={example.url}
          target="_blank"
          rel="noreferrer"
          className="text-muted-foreground hover:text-foreground"
          aria-label="Source page"
        >
          <ExternalLinkIcon className="h-3.5 w-3.5" />
        </a>
      ) : null}
    </li>
  );
};
